import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject, Subscription } from 'rxjs';
import { DonutChartComponent, DonutChartData } from '../../../../../_shared/components/graphs/donut-chart/donut-chart.component';
import { CostTrackingService } from '../../../services/cost-tracking.service';
import { FilterService } from '../../../../../_shared/services/filter.service';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { WidgetFilterData } from '../../../../../_shared/models/filters/widget-filter-data.model';
import { EmptyStateComponent } from '../../../../../_shared/components/states/empty-state/empty-state.component';
import { formatCost } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { AuthService } from '../../../../../_shared/services/auth.service';
import { TranslocoService, TranslocoDirective } from '@jsverse/transloco';
import { hasCompleteDateRange } from '../../../../../_shared/helpers/widget-filter.helper';
import { AccountDateFilterRequest } from '../../../../../_shared/models/account-date-filter-request.model';

@Component({
  selector: 'app-cost-distribution-reactive-preventive',
  standalone: true,
  imports: [CommonModule, DonutChartComponent, EmptyStateComponent, TranslocoDirective],
  templateUrl: './cost-distribution-reactive-preventive.component.html',
  styleUrl: './cost-distribution-reactive-preventive.component.scss'
})
export class CostDistributionReactivePreventiveComponent implements OnInit, OnDestroy {
  private costTrackingService: CostTrackingService = inject(CostTrackingService);
  private filterService: FilterService = inject(FilterService);
  private errorHandlerService: ErrorHandlerService = inject(ErrorHandlerService);
  private cancelSubject: Subject<void> = new Subject<void>();
  private authService = inject(AuthService);

  title: string = inject(TranslocoService).translate('costTracking.charts.costDistributionReactiveVsPreventive');
  isLoading: boolean = true;
  widgetFilter: WidgetFilterData;
  currencySymbol: string = '';
  currencyCode: string = '';

  chartData: DonutChartData[] = [];
  hoveredPercentage: number | null = null;
  hoveredSegmentName: string | null = null;

  // Stats
  reactivePercentage: number = 0;
  reactiveCost: number = 0;
  reactiveCount: number = 0;
  preventivePercentage: number = 0;
  preventiveCost: number = 0;
  preventiveCount: number = 0;

  private filterSubscription: Subscription;

  constructor() {
    this.filterSubscription = this.filterService.filterUpdated$.subscribe(async (filter) => {
      this.widgetFilter = filter;
      await this.loadData();
    });
    this.currencyCode = this.authService.getUserCurrencyCode();
    this.currencySymbol = this.authService.getUserCurrencySymbol();
  }

  ngOnInit(): void {
    this.widgetFilter = this.filterService.filter;
    void this.loadData();
  }

  ngOnDestroy(): void {
    this.filterSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  private async loadData(): Promise<void> {
    if (!hasCompleteDateRange(this.widgetFilter)) return;

    this.isLoading = true;

    try {
      this.cancelSubject.next();
      const request: AccountDateFilterRequest = {
        accountIds: this.widgetFilter.accountIds ?? [],
        timeFrom: this.widgetFilter.startDate,
        timeTo: this.widgetFilter.endDate,
        interval: this.widgetFilter.interval,
      };
      const response = await this.costTrackingService.getCostDistributionSummary(
        request,
        this.cancelSubject
      );

      this.preventivePercentage = response.data?.preventivePercentage ?? 0;
      this.reactivePercentage = response.data?.reactivePercentage ?? 0;
      this.preventiveCost = response.data?.preventiveCost ?? 0;
      this.reactiveCost = response.data?.reactiveCost ?? 0;
      this.preventiveCount = response.data?.servicesCompletedCount ?? 0;
      this.reactiveCount = response.data?.unplannedRepairsCount ?? 0;
      this.chartData = [
        { name: 'Preventive', value: this.preventivePercentage, color: '#66AECF' },
        { name: 'Reactive', value: this.reactivePercentage, color: '#F4BC2B' }
      ];

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: () => {
          this.isLoading = false;
        }
      });
    }
  }

  formatCost(value: number): string {
    return formatCost(value, this.currencySymbol);
  }

  hasData(): boolean {
    return this.chartData.length > 0;
  }

  onSegmentHover(event: { percentage: number; name: string } | null): void {
    if (event) {
      this.hoveredPercentage = event.percentage;
      this.hoveredSegmentName = event.name;
    } else {
      this.hoveredPercentage = null;
      this.hoveredSegmentName = null;
    }
  }

  getCenterPercentage(): string {
    return this.hoveredPercentage !== null ? `${this.hoveredPercentage}%` : 'Percentage';
  }
}